
import { useState } from 'react';

export function CalculatorSection() {
  const [currentBill, setCurrentBill] = useState('');
  const [increase, setIncrease] = useState('');
  const [result, setResult] = useState<{
    monthly: number;
    yearly: number;
    twoYears: number;
    normalBill: number;
  } | null>(null);

  const calculate = () => {
    const bill = parseFloat(currentBill);
    const inc = parseFloat(increase) / 100;
    
    if (bill <= 0 || inc <= 0) return;
    
    const normalBill = bill / (1 + inc);
    const wasted = bill - normalBill;
    
    setResult({
      monthly: wasted,
      yearly: wasted * 12,
      twoYears: wasted * 24,
      normalBill: normalBill
    });
  };

  const sendWhatsApp = () => {
    if (!result) return;
    
    const message = `Olá! Calculei meu prejuízo com vazamento:

💰 Conta atual: R$ ${parseFloat(currentBill).toFixed(2)}
📈 Aumento: ${increase}%
💸 Perdendo R$ ${result.monthly.toFixed(2)} por mês
🚨 Prejuízo anual: R$ ${result.yearly.toFixed(2)}

Preciso de uma detecção urgente! Quando podem vir?`;

    window.open(`https://wa.me/5511948948561?text=${encodeURIComponent(message)}`, '_blank');
  };

  const resetCalculator = () => {
    setCurrentBill('');
    setIncrease('');
    setResult(null);
  };

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-br from-blue-50 via-white to-orange-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            💰 Calcule Quanto Você Está Perdendo
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Descubra o prejuízo real que um vazamento está causando na sua conta de água
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 border border-slate-200 animate-fade-in-up">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700" htmlFor="current-bill">
                  💧 Valor da conta atual (R$)
                </label>
                <input
                  type="number"
                  id="current-bill"
                  placeholder="Ex: 180,00"
                  value={currentBill}
                  onChange={(e) => setCurrentBill(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-lg font-medium"
                />
                <p className="text-xs text-slate-500">Digite o valor da sua última conta</p>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700" htmlFor="increase">
                  📈 Aumento observado (%)
                </label>
                <input
                  type="number"
                  id="increase"
                  placeholder="Ex: 40"
                  value={increase}
                  onChange={(e) => setIncrease(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-lg font-medium"
                />
                <p className="text-xs text-slate-500">Comparado com o valor normal</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <button
                onClick={calculate}
                disabled={!currentBill || !increase}
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-slate-400 disabled:to-slate-500 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-calculator-line mr-2"></i>
                Calcular Meu Prejuízo
              </button>
              
              {result && (
                <button
                  onClick={resetCalculator}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-slate-500 hover:bg-slate-600 text-white font-medium py-4 px-6 sm:px-8 rounded-lg transition-colors text-sm sm:text-base text-center"
                >
                  <i className="ri-refresh-line text-lg sm:text-xl"></i>
                  <span className="leading-snug">Nova Simulação</span>
                </button>
              )}
            </div>

            {result && (
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-6 animate-fade-in">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-red-600 mb-2">
                    🚨 ALERTA DE PREJUÍZO DETECTADO!
                  </h3>
                  <p className="text-slate-600">
                    Veja quanto dinheiro você está perdendo:
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white rounded-lg p-4 text-center border border-red-200">
                    <div className="text-2xl font-bold text-red-600">
                      R$ {result.monthly.toFixed(2)}
                    </div>
                    <div className="text-sm text-slate-600 font-medium">Por mês</div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 text-center border border-red-200">
                    <div className="text-2xl font-bold text-red-600">
                      R$ {result.yearly.toFixed(2)}
                    </div>
                    <div className="text-sm text-slate-600 font-medium">Por ano</div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 text-center border border-red-200">
                    <div className="text-2xl font-bold text-red-600">
                      R$ {result.twoYears.toFixed(2)}
                    </div>
                    <div className="text-sm text-slate-600 font-medium">Em 2 anos</div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
                  <div className="flex items-start gap-3">
                    <i className="ri-lightbulb-line text-blue-600 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-1">💡 Análise do Especialista:</h4>
                      <p className="text-blue-700 text-sm">
                        Sua conta deveria ser aproximadamente <strong>R$ {result.normalBill.toFixed(2)}</strong>. 
                        O custo da detecção e reparo é muito menor que continuar pagando contas altas!
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <button
                    onClick={sendWhatsApp}
                    className="w-full sm:w-auto inline-flex flex-wrap items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-4 px-6 sm:px-8 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl text-sm sm:text-base text-center"
                  >
                    <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
                    <span className="leading-snug">🆘 Pare de Perder Dinheiro Agora!</span>
                  </button>
                  
                  <p className="text-xs text-slate-500 mt-3">
                    Clique para enviar seus dados via WhatsApp e agendar uma visita gratuita
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Seção de Benefícios */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in-up">
            <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-slate-200">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Economia Garantida</h3>
              <p className="text-sm text-slate-600">
                O investimento na detecção se paga em poucos meses com a economia na conta
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-slate-200">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Solução Rápida</h3>
              <p className="text-sm text-slate-600">
                Detectamos e reparamos em 1 dia, acabando com o desperdício imediatamente
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-slate-200">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-orange-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Sem Quebra</h3>
              <p className="text-sm text-slate-600">
                Tecnologia avançada localiza o vazamento sem destruir sua casa
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
